import { Button } from '@avanta/shared/components/ui/Button';
import { Card } from '@avanta/shared/components/ui/Card';
import { SelectController } from '@avanta/shared/components/ui/Select';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { UserType } from '@avanta/shared/constants/users';
import { i18nService } from '@avanta/shared/services/i18n';
import { type User } from '@avanta/shared/types/users';
import { languageOptions } from '@avanta/shared/utils/common';
import { timezoneOptions } from '@avanta/shared/utils/date';
import { Globe, Languages, Pencil, Save, X } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useUpdateAdminPreferences } from 'hooks/useUpdateAdminPreferences';
import { useUpdateEnterprisePreferences } from 'hooks/useUpdateEnterprisePreferences';
import { useUserStore } from 'stores/user';
import { type FormData } from 'types/form';

export const Preferences = (): React.ReactElement | null => {
  const { t } = useTranslation(LOCIZE_NS.PROFILE);
  const user = useUserStore(state => state.user as User);
  const [isEditing, setIsEditing] = useState(false);
  const { mutateAsync: updateAdminPreferences } = useUpdateAdminPreferences();
  const { mutateAsync: updateEnterprisePreferences } = useUpdateEnterprisePreferences();
  const {
    handleSubmit,
    control,
    reset,
    formState: { isSubmitting, isValid, isDirty },
  } = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      language: user?.appLanguage || '',
      timeZone: user?.timeZone || '',
    },
  });
  const isSubmitDisabled = !isValid || isSubmitting || !isDirty;

  const handleOnCancel = (): void => {
    reset({
      language: user.appLanguage,
      timeZone: user.timeZone,
    });
    setIsEditing(false);
  };

  const handleOnSubmit = async (data: FormData): Promise<void> => {
    try {
      const payload = {
        appLanguage: String(data.language),
        timeZone: String(data.timeZone),
      };

      if (user.type === UserType.ADMIN_ENTERPRISE) {
        await updateAdminPreferences(payload);
      } else {
        await updateEnterprisePreferences(payload);
      }

      i18nService.changeLanguage(String(data.language));
      reset({
        language: data.language,
        timeZone: data.timeZone,
      });
      setIsEditing(false);
    } catch {
      toast.error(`${t('Profile.preferences.heading')}: ${t('ChangeLanguage.error')}`);
    }
  };

  return (
    <Card className="bg-white p-0 am-md:p-0" title={t('Profile.preferences.heading')} icon={Globe}>
      <form onSubmit={handleSubmit(handleOnSubmit)}>
        <div className="grid grid-cols-1 gap-6 px-8 pt-8 pb-6 am-lg:grid-cols-2">
          <SelectController
            className={isEditing ? 'w-full' : 'w-full pointer-events-none [&>svg]:hidden'}
            control={control}
            name="language"
            label={t('ChangeLanguage.form.language.label')}
            placeholder={t('ChangeLanguage.form.language.placeholder')}
            options={languageOptions}
            leftIcon={Languages}
          />
          <SelectController
            className={isEditing ? 'w-full' : 'w-full pointer-events-none [&>svg]:hidden'}
            control={control}
            name="timeZone"
            label={t('ProfileSettings.Form.timezone.label')}
            placeholder={t('ProfileSettings.Form.timezone.placeholder')}
            options={timezoneOptions}
            leftIcon={Globe}
          />
        </div>
        {isEditing ? (
          <div className="flex w-full justify-end gap-4 pr-4 pb-8">
            <Button
              type="button"
              variant="destructive_terciary"
              size="lg"
              icon="leading"
              onClick={handleOnCancel}
            >
              <X className="size-4" />
              {t('ChangeLanguage.cancelEdit')}
            </Button>
            <Button
              type="submit"
              variant="secondary"
              size="lg"
              icon="leading"
              disabled={isSubmitDisabled}
              isLoading={isSubmitting}
            >
              <Save className="size-4" />
              {t('ChangeLanguage.saveChanges')}
            </Button>
          </div>
        ) : (
          <div className="flex w-full justify-end pr-4 pb-3">
            <Button
              type="button"
              variant="ghost"
              icon="leading"
              className="text-teal-600 text-sm leading-4.5 font-medium"
              onClick={() => setIsEditing(true)}
            >
              <Pencil className="text-teal-600 size-4" />
              {t('ChangeLanguage.editPreferences')}
            </Button>
          </div>
        )}
      </form>
    </Card>
  );
};
